// Shows a list of tasks. Has an URL of the form:
//    /item/:itemId/tasks/:taskList
// taskList = 'all' all tasks for the given ItemId
//          = 'overdue' all overdue tasks
//          = 'soon' for coming soon tasks.
//
// When called from another page, can include state so don't need to reload
// everything. state contains:
//    name - Name of this list (for breadcrumbs)
//    item - Item the tasks belong to
//    bucket - Bucket to show links to (see utilities/TaskBuckets)

import { DataCard, CardHeader, HourMeter } from '../../components';
import { ShareTasks } from './ShareTasks';
import { useParams } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { addBucketNameToTasks, taskUrlToBucketName, bucketHeading } from '../../fleet-shared/TaskBuckets';
import { bucketNameAll, urlForTasksAssignedTo } from '../../fleet-shared/Urls.mjs';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import { TaskTable } from './TaskTable';
import { useTaskQuery } from './TaskQuery';
import { useStatusMessage } from '../../components';
// Use the minimal local create-task mutation from Database.mjs (avoids the
// nested @hasOne `item`/`itemCounter` selection set that triggers per-field
// owner auth errors on Amplify GraphQL Transformer v2 mutation responses).
import { APICreateTask, updateCounter, deleteCounter } from '../../utilities/Database.mjs';
import { useEditTaskPopup } from './useEditTaskPopup';
import { useTaskPhotos } from './useTaskPhotos';
import { useTaskViewPrefs } from './useTaskViewPrefs';
import { TaskViewControls } from './TaskViewControls';
import { PhotoErrorAlert } from '../logbook/PhotoErrorAlert';

// API
import { generateClient } from 'aws-amplify/api';
const client = generateClient();

export const SortOverdue = 'o';
export const SortAssigned = 'a';
export const SortPriority = 'p';

const SortSelection = ({ sortDirection, setSortDirection }) => {
  return (
    <div className="btn-group">
      <button
        className={`btn btn-sm ${sortDirection === SortOverdue ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setSortDirection(SortOverdue)}
      >
        Overdue First (Date then Counter)
      </button>
      <button
        className={`btn btn-sm ${sortDirection === SortAssigned ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setSortDirection(SortAssigned)}
      >
        Assigned To (then priority)
      </button>
      <button
        className={`btn btn-sm ${sortDirection === SortPriority ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setSortDirection(SortPriority)}
      >
        Priority
      </button>
    </div>
  );
}




const Tasks = () => {
  // Passed on URL, the item the task(s) belong to. Optionally, the single taskId being shown.
  const { itemId, listType } = useParams();
  const [item, setItem] = useState(null);

  // User can sort the tasks by different criteria.
  const [sortDirection, setSortDirection] = useState(SortOverdue);

  const [ showShare, setShowShare ] = useState(false);

  // Does the query to get the item and its tasks.
  const fetchItems = useTaskQuery(itemId, listType, sortDirection, setItem);

  // For showing status messages.
  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  const [entries, setEntries] = useState(null);

  // One S3 listing of `${itemId}/task/` for the whole page. The switch IS the
  // hook's `enabled` and IS what the table is given, one boolean, so "we
  // fetched" and "we render" cannot disagree and turning it off is a mount gate
  // on the read. Off does not clear the map and back on does not refetch; a
  // failure leaves the read unmarked, so off-and-on is the retry.
  const [viewPrefs, setViewPref] = useTaskViewPrefs();
  const { photosByEntry, photosError, openPhoto } = useTaskPhotos(itemId, {
    enabled: viewPrefs.showPhotos,
  });

  const taskEdited = useCallback(() => {
    setEntries((prev) => (prev ? [...prev] : prev));
  }, []);

  const taskDeleted = useCallback(() => {
    setEntries((prev) => (prev ? [...prev] : prev));
  }, []);

  const { showEditTaskPopup, TaskEditor } = useEditTaskPopup({
    item,
    taskEdited,
    taskDeleted,
    taskListModel: entries,
  });

  // User hit the add button, add a new task and open the edit popup (same as the item page).
  const addTapped = useCallback(async () => {
    if (!item) {
      showError(new Error('Still loading this item. Try again in a moment.'));
      return;
    }
    try {
      showText('Creating new task...');

      let newTask = {
        name: 'New Task',
        itemId: itemId,
        priority: 0,
        assignedEmail: '',
        rank: new Date().getDate(),
        dueDate: null
      };

      const created = await client.graphql({ query: APICreateTask, variables: { input: newTask }});
      var task = created.data.createTaskPogoTask;

      hideStatus();

      addBucketNameToTasks([task], item);
      setEntries((prev) => (prev ? [...prev, task] : [task]));
      showEditTaskPopup(task);
    } catch (err) {
      showError(err);
    }
  }, [showText, hideStatus, showError, itemId, item, showEditTaskPopup]);


  // User hit the share button, show the sharing popup.
  const shareTapped = useCallback(() => {
    setShowShare(true);
  }, []);


  // Passed to HourMeter so clicking a counter row opens the Edit Hours modal.
  // Mirrors the handlers on the Item page; both mutate the same
  // item.itemCounter.items model in place. Can throw; HourMeter catches.
  const saveCounter = useCallback(async (counter) => {
    await updateCounter(counter, item.itemCounter.items);
  }, [item]);

  const deleteCounterItem = useCallback(async (id) => {
    await deleteCounter(id, item.itemCounter.items);
  }, [item]);


  // This is a function for creating test data. It's not used in production. I use it
  // to create a ton of tasks, so that nextToken comes in to play on database queries.
  // function createTestTasks() {
  //   let tasks = [];
  //   for (var i=100; i < 1000; i++) {
  //     let task = createTask(item, `Task ${i+1}`);
  //     tasks.push(task);
  //   }
  //   return tasks;
  // }

  const onHide = useCallback(() => { setShowShare(false); }, []);

  // Each row's assigned name links to that person's list on this item.
  const assigneeLinkFor = useCallback(
    (task) => urlForTasksAssignedTo(itemId, task.assignedEmail),
    [itemId]
  );


  return (
    <div className='pb-2'>
      <ItemBreadcrumbs item={item} />
      <StatusMessage />

      <ShareTasks show={showShare} onHide={onHide} entries={entries} itemId={itemId} itemName={item?.name} listType={listType} />

      {TaskEditor}

      {item && item.itemCounter.items && item.itemCounter.items.length > 0 && (
        <HourMeter
          itemId={itemId}
          counterItems={item.itemCounter.items}
          save={saveCounter}
          delete={deleteCounterItem}
          />
      )}

      <DataCard
        header={
          <CardHeader 
            text={bucketHeading(taskUrlToBucketName(listType), item)}
            belowText={
              <div className="d-flex align-items-center flex-wrap gap-3">
                <SortSelection sortDirection={sortDirection} setSortDirection={setSortDirection} />
                <TaskViewControls
                  showPhotos={viewPrefs.showPhotos}
                  onShowPhotosChange={(next) => setViewPref('showPhotos', next)}
                />
              </div>
            }
            addTapped={addTapped} 
            shareTapped={shareTapped}
          />}
        body={
          <div className='tasks-container'>
            {/* Never the page's StatusMessage: that belongs to the tasks read,
                and an unreadable S3 prefix must not look like a page that
                failed to load. */}
            <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} subject="tasks" />
            <TaskTable
              fetchItems={fetchItems}
              entries={entries}
              setEntries={setEntries}
              tasksListBucket={listType ?? bucketNameAll}
              sortOverdueFirst={sortDirection === SortOverdue}
              assigneeLinkFor={assigneeLinkFor}
              showPhotos={viewPrefs.showPhotos}
              photosByEntry={photosByEntry}
              onOpenPhoto={openPhoto}
            />
            <button type="button" className='btn btn-success mt-2' onClick={addTapped}>Add</button>
            <StatusMessage />
          </div>
        }
      />
    </div>
  );
}

export default Tasks;
